import { ApolloProvider } from '@apollo/client';
import React, { useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { Navigate, RouterProvider, createBrowserRouter } from 'react-router-dom';
import { AppShell } from './components/AppShell.web.jsx';
import { LoginScreen } from './components/LoginScreen.web.jsx';
import { apolloClient } from './lib/apollo.js';
import { AuthProvider, useAuth } from './lib/auth.jsx';
import { SessionProvider, useSession } from './lib/session.jsx';
import { ShortcutProvider } from './lib/shortcuts.jsx';
import { CalendarScreen } from './screens/Calendar.web.jsx';
import { ContactDetailScreen } from './screens/ContactDetail.web.jsx';
import { DashboardScreen } from './screens/Dashboard.web.jsx';
import { ContactLookupMobile } from './screens/ContactLookup.mobile.jsx';
import { useIsMobile } from './lib/viewport.js';
import { ContactsScreen } from './screens/Contacts.web.jsx';
import { FollowUpsScreen } from './screens/FollowUps.web.jsx';
import { CampaignsScreen } from './screens/messaging/Campaigns.web.jsx';
import { OutboxScreen } from './screens/messaging/Outbox.web.jsx';
import { IssueDetailScreen } from './screens/issues/IssueDetail.web.jsx';
import { IssueReportsScreen } from './screens/issues/IssueReports.web.jsx';
import { IssuesScreen } from './screens/issues/Issues.web.jsx';
import { AutomationScreen } from './screens/settings/Automation.web.jsx';
import { WhatsappScreen } from './screens/settings/Whatsapp.web.jsx';
import { ChecklistsScreen } from './screens/settings/Checklists.web.jsx';
import { CustomFieldsScreen } from './screens/settings/CustomFields.web.jsx';
import { IntegrationsScreen } from './screens/settings/Integrations.web.jsx';
import { ImportScreen } from './screens/settings/Import.web.jsx';
import { ActivityTypesScreen } from './screens/settings/ActivityTypes.web.jsx';
import { IssueTypesScreen } from './screens/settings/IssueTypes.web.jsx';
import { LookupsScreen } from './screens/settings/Lookups.web.jsx';
import { SettingsLayout } from './screens/settings/SettingsLayout.web.jsx';
import { SpecialtiesScreen } from './screens/settings/Specialties.web.jsx';
import { TagsScreen } from './screens/settings/Tags.web.jsx';
import { TemplatesScreen } from './screens/settings/Templates.web.jsx';
import { UsersScreen } from './screens/settings/Users.web.jsx';
import { SystemScreen } from './screens/settings/System.web.jsx';
import '@meshhub/design-tokens/tokens/tokens.css';
import './styles.css';

/**
 * Desktop-First: `.web.jsx` is the canonical variant. A future `.mobile.jsx`
 * is a *reduction* of it, never a fork (CLAUDE.md §12).
 */
/**
 * The two routes that have a mobile variant.
 *
 * Chosen at RENDER rather than at route definition, so rotating a phone or
 * resizing a window switches without a reload — which is also how anybody
 * tests this.
 */
function ContactsRoute() {
  return useIsMobile() ? <ContactLookupMobile /> : <ContactsScreen />;
}

/**
 * The landing page. A rep opening MeshHub on a phone at a congress wants the
 * lookup, not a dashboard they cannot read; on a desk they want the dashboard.
 */
function HomeRoute() {
  return useIsMobile() ? <ContactLookupMobile /> : <DashboardScreen />;
}

const routes = [
  {
    path: '/',
    element: <AppShell />,
    children: [
      // The dashboard is the POST-LOGIN LANDING PAGE as of Step 6: the
      // first question anybody has on opening a CRM is how it is going,
      // and the contact list is one click away for the people whose job
      // is the list.
      { index: true, element: <HomeRoute /> },
      { path: 'dashboard', element: <DashboardScreen /> },
      // THE ONLY TWO MOBILE REDUCTIONS (CLAUDE.md §12). On a narrow viewport
      // the contact list becomes a lookup-and-call screen and quick-add
      // becomes a full-screen sheet, because exhibitions happen on phones.
      // Every OTHER route renders the desktop layout unchanged — the calendar
      // and the dashboard are desk work that a phone would make worse, and a
      // half-built mobile version of them would be worse still.
      { path: 'contacts', element: <ContactsRoute /> },
      { path: 'contacts/:id', element: <ContactDetailScreen /> },
      { path: 'calendar', element: <CalendarScreen /> },
      { path: 'followups', element: <FollowUpsScreen /> },
      // Step 5. The outbox is the default messaging screen because it is the
      // one anyone opens after pressing send.
      { path: 'messaging', element: <OutboxScreen /> },
      { path: 'messaging/outbox', element: <OutboxScreen /> },
      { path: 'messaging/campaigns', element: <CampaignsScreen /> },
      { path: 'issues', element: <IssuesScreen /> },
      // Before `issues/:id`, or "reports" would be read as an issue id.
      { path: 'issues/reports', element: <IssueReportsScreen /> },
      { path: 'issues/:id', element: <IssueDetailScreen /> },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          { index: true, element: <SettingsIndex /> },
          { path: 'lookups', element: <LookupsScreen /> },
          { path: 'specialties', element: <SpecialtiesScreen /> },
          { path: 'tags', element: <TagsScreen /> },
          { path: 'custom-fields', element: <CustomFieldsScreen /> },
          { path: 'templates', element: <TemplatesScreen /> },
          { path: 'checklists', element: <ChecklistsScreen /> },
          // The two vocabularies that live in OTHER services, and therefore
          // cannot be folded into /settings/lookups' kind picker.
          { path: 'activity-types', element: <ActivityTypesScreen /> },
          { path: 'issue-types', element: <IssueTypesScreen /> },
          // Self-service for EVERY role, unlike the rest of /settings: a Google
          // connection is one person's own calendar. SettingsLayout knows.
          { path: 'integrations', element: <IntegrationsScreen /> },
          { path: 'automation', element: <AutomationScreen /> },
          { path: 'whatsapp', element: <WhatsappScreen /> },
          { path: 'import', element: <ImportScreen /> },
          { path: 'users', element: <UsersScreen /> },
          { path: 'system', element: <SystemScreen /> },
        ],
      },
    ],
  },
];

/**
 * `/settings` lands somewhere the caller may actually go.
 *
 * A fixed redirect to `lookups` would drop a USER on the forbidden screen for
 * clicking their own integrations link — the nav is role-aware, so the default
 * has to be too.
 */
function SettingsIndex() {
  const { isAdmin, loading } = useSession();
  if (loading) return null;
  return <Navigate to={isAdmin ? 'lookups' : 'integrations'} replace />;
}

/**
 * While unauthenticated, every route resolves to the login screen — the router
 * is not even mounted, so no screen can fire a query without a token.
 */
function Root() {
  const { status } = useAuth();

  /**
   * Built lazily, on the first authenticated render. `createBrowserRouter`
   * snapshots `window.location` when it is constructed, and the OIDC callback
   * rewrites the URL back to the requested path while still unauthenticated —
   * so a module-scope router would capture the callback URL and land everyone
   * on the home page instead of their deep link.
   */
  const router = useMemo(
    () => (status === 'authenticated' ? createBrowserRouter(routes) : null),
    [status],
  );

  if (status !== 'authenticated' || !router) return <LoginScreen />;

  return (
    <ApolloProvider client={apolloClient}>
      <SessionProvider>
        <ShortcutProvider>
          <RouterProvider router={router} />
        </ShortcutProvider>
      </SessionProvider>
    </ApolloProvider>
  );
}

createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AuthProvider>
      <Root />
    </AuthProvider>
  </React.StrictMode>,
);
